import { inject, Injectable } from '@angular/core';
import { environment } from '@environments';
import { ThemeService } from './theme.service';

declare global {
    interface Window {
        google?: any;
    }
}

/** Limites de largura aceitos pelo `renderButton` do Google Identity Services. */
const MIN_BUTTON_WIDTH = 200;
const MAX_BUTTON_WIDTH = 400;
const FALLBACK_BUTTON_WIDTH = 320;

@Injectable({
    providedIn: 'root',
})
export class GoogleIdentityService {
    private readonly _webClientId = environment.GOOGLE_WEB_CLIENT_ID;
    private _scriptPromise: Promise<void> | null = null;

    // * [Injectors]
    private _themeService = inject(ThemeService);

    public async renderSignInButton(
        targetElement: HTMLElement | null | undefined,
        onCredential: (idToken: string) => void
    ): Promise<void> {
        if (!targetElement) return;
        await this._loadGoogleScript();

        if (!window.google?.accounts?.id) {
            throw new Error('Google Identity Services not available');
        }

        window.google.accounts.id.initialize({
            client_id: this._webClientId,
            callback: (response: { credential?: string }) => {
                if (response?.credential) onCredential(response.credential);
            },
            auto_select: false,
            cancel_on_tap_outside: true,
        });

        targetElement.innerHTML = '';
        window.google.accounts.id.renderButton(targetElement, {
            type: 'standard',
            theme: this._themeService.currentTheme() === 'dark' ? 'filled_black' : 'outline',
            size: 'large',
            shape: 'pill',
            text: 'continue_with',
            width: this._resolveButtonWidth(targetElement),
        });
    }

    /**
     * Calcula a largura do botão a partir do espaço real disponível no container.
     * O botão personalizado ("Continuar como...") é renderizado num iframe do Google que
     * pinta o próprio fundo: se a largura passar do container, esse fundo vaza pra fora
     * do card e aparece como uma faixa atrás do botão.
     * @param targetElement Elemento que hospeda o botão.
     * @returns Largura em px, limitada ao intervalo aceito pelo Google.
     */
    private _resolveButtonWidth(targetElement: HTMLElement): number {
        const available =
            targetElement.getBoundingClientRect().width ||
            targetElement.parentElement?.getBoundingClientRect().width ||
            FALLBACK_BUTTON_WIDTH;

        return Math.round(
            Math.min(
                MAX_BUTTON_WIDTH,
                Math.max(MIN_BUTTON_WIDTH, available || FALLBACK_BUTTON_WIDTH)
            )
        );
    }

    private _loadGoogleScript(): Promise<void> {
        if (this._scriptPromise) return this._scriptPromise;

        this._scriptPromise = new Promise((resolve, reject) => {
            const existingScript = document.querySelector(
                'script[src="https://accounts.google.com/gsi/client"]'
            ) as HTMLScriptElement | null;
            if (existingScript) {
                if (window.google?.accounts?.id) resolve();
                else existingScript.addEventListener('load', () => resolve(), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google Identity script'));
            document.head.appendChild(script);
        });

        return this._scriptPromise;
    }
}
